import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { EventService, GEOFENCE_EVENT_TYPES, mapExitToDisconnectEvent, RowEventType } from 'map/weavix-map/event-log/event.service';
import { Subject, Subscription } from 'rxjs';
import { AnyBadgeEvent } from '@weavix/models/src/badges/event';
import { Person } from 'weavix-shared/models/person.model';
import { Topic } from '@weavix/models/src/topic/topic';
import { Geofence } from 'weavix-shared/models/weavix-map.model';
import { AccountService } from 'weavix-shared/services/account.service';
import { AlertService, ServiceError } from 'weavix-shared/services/alert.service';
import { BadgeService } from 'weavix-shared/services/badge.service';
import { PersonService } from 'weavix-shared/services/person.service';
import { PubSubService } from 'weavix-shared/services/pub-sub.service';
import { TranslationService } from 'weavix-shared/services/translation.service';
import { AutoUnsubscribe } from 'weavix-shared/utils/utils';
import { LoadingComponent } from 'components/loading/loading.component';
import { CommonModule } from '@angular/common';
import { IconComponent } from 'components/icon/icon.component';
import { EventLogComponent } from 'map/weavix-map/event-log/event-log.component';
import { TranslateModule } from '@ngx-translate/core';

@AutoUnsubscribe()
@Component({
    selector: 'app-geofence-detail',
    templateUrl: './geofence-detail.component.html',
    styleUrls: ['./geofence-detail.component.scss', '../map-detail-view.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,

        EventLogComponent,
        IconComponent,
        LoadingComponent,
    ],
})
export class GeofenceDetailComponent implements OnInit, OnChanges, OnDestroy {
    @Input() geofence: Geofence;
    @Input() people: Person[];
    @Output() closeOutput = new EventEmitter<void>();

    allPeopleMap = new Map<string, Person>();
    badgeEventSubscription: Subscription;
    peopleOverviewDataSub: Subscription;
    badgeEventsHistory: AnyBadgeEvent[] = [];
    rowEventTypes: RowEventType[] = [...GEOFENCE_EVENT_TYPES];
    badgeEvents = new Subject<AnyBadgeEvent>();
    companiesInArea = new Map<string, string>();
    craftsInArea = new Map<string, string>();
    loading: boolean = true;
    isError: boolean = false;

    constructor(
        private readonly accountService: AccountService,
        private readonly alertService: AlertService,
        private readonly personService: PersonService,
        private readonly pubSubService: PubSubService,
        private readonly badgeService: BadgeService,
        private readonly translationService: TranslationService,
        private readonly cdr: ChangeDetectorRef,
    ) { }

    async ngOnInit() {
        await this.setup();
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.geofence?.currentValue?.id !== changes.geofence?.previousValue?.id) {
            await this.setup();
            this.cdr.markForCheck();
        } else if (changes.people?.currentValue?.length !== changes.people?.previousValue?.length) {
            this.setPeopleInsideGeoStats();
            this.cdr.markForCheck();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribeFromGeofenceSubs();
    }

    private async setup() {
        try {
            this.loading = true;
            this.isError = false;
            await this.setAllPeopleMap();
            this.setPeopleInsideGeoStats();
            await this.setupEventLog();
        } catch (e) {
            this.isError = true;
        } finally {
            this.loading = false;
        }
    }

    private async setAllPeopleMap() {
        try {
            const people = await this.personService.getPeople(this);
            people.forEach(p => this.allPeopleMap.set(p.id, p));
        } catch (e) {
            this.alertService.sendServiceError(e, ServiceError.Get, 'person.people');
        }
    }

    private setPeopleInsideGeoStats(): void {
        this.clearInsideGeoStats();
        this.people.forEach(p => {
            const person = this.allPeopleMap.get(p.id);
            if (person) {
                this.companiesInArea.set(person.companyId, person.companyId);
                person.crafts?.forEach(c => this.craftsInArea.set(c, c));
            }
        });
    }

    private clearInsideGeoStats(): void {
        this.companiesInArea.clear();
        this.craftsInArea.clear();
    }

    private async setupEventLog() {
        try {
            this.badgeEventsHistory = await this.badgeService.getEventsByGeofence(this, this.geofence.id).then(mapExitToDisconnectEvent);
            await this.setupGeofenceSubscriptions();
        } catch (e) {
            console.error(e);
        }
    }

    private async setupGeofenceSubscriptions() {
        this.unsubscribeFromGeofenceSubs();
        await this.subscribToGeofenceEvents();
    }

    private async subscribToGeofenceEvents() {
        const account = await this.accountService.getAccount();
        if (!account) return;

        this.badgeEventSubscription = (await this.pubSubService.subscribe<AnyBadgeEvent>(this, Topic.AccountPersonBadgeEvent, [account.id, '+']))
            .subscribe(async message => {
                if (this.isGeofenceEvent(message.payload)) this.badgeEvents.next(message.payload);
                this.cdr.markForCheck();
            });
    }

    private isGeofenceEvent(event: AnyBadgeEvent) {
        const fenceId = event?.geoFenceId;
        return fenceId === this.geofence.id && EventService.isGeofenceBadgeEvent(event);
    }

    private unsubscribeFromGeofenceSubs(): void {
        this.badgeEventSubscription?.unsubscribe();
    }

    handleClose(): void {
        this.closeOutput.emit();
    }

    eventPersonNameLookUp() {
        return <T extends AnyBadgeEvent>(row: T) => {
            if (row.personName) {
                return row.personName;
            } else {
                const person: Person = this.allPeopleMap.get(row.personId);
                return person ? person.fullName : this.translationService.getImmediate('generics.unknown');
            }
        };
    }
}
